import { FC, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid2 as Grid,
  SxProps,
  SelectChangeEvent,
  InputAdornment,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

import {
  useSearch,
  SEARCH_PARAM_COMPENSATION_MIN,
  SEARCH_PARAM_COMPENSATION_MAX,
  SEARCH_PARAM_COMPENSATION_TYPE,
} from '@carcareers/contexts';

interface JobSearchCompensationInputProps {
  sx?: SxProps;
}

export const JobSearchCompensationInput: FC<JobSearchCompensationInputProps> = ({ sx }) => {
  const {
    compensationMin: compensationMinContext,
    compensationMax: compensationMaxContext,
    compensationType: compensationTypeContext,
    updateSearchParams,
  } = useSearch();

  const [compensationMin, setCompensationMin] = useState(compensationMinContext);
  const [compensationMax, setCompensationMax] = useState(compensationMaxContext);
  const [compensationType, setCompensationType] = useState(compensationTypeContext || 'any');

  const handleCompensationMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCompensationMin(value);
    updateSearchParams(SEARCH_PARAM_COMPENSATION_MIN, value);
  };

  const handleCompensationMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCompensationMax(value);
    updateSearchParams(SEARCH_PARAM_COMPENSATION_MAX, value);
  };

  const handleCompensationTypeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setCompensationType(value);
    updateSearchParams(SEARCH_PARAM_COMPENSATION_TYPE, value === 'any' ? '' : value);
  };

  useEffect(() => {
    if (compensationMinContext !== compensationMin) {
      setCompensationMin(compensationMinContext);
    }
  }, [compensationMinContext]);

  useEffect(() => {
    if (compensationMaxContext !== compensationMax) {
      setCompensationMax(compensationMaxContext);
    }
  }, [compensationMaxContext]);

  useEffect(() => {
    const contextValue = compensationTypeContext || 'any';
    if (contextValue !== compensationType) {
      setCompensationType(contextValue);
    }
  }, [compensationTypeContext]);

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <Typography variant="h6" gutterBottom>Compensation</Typography>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="compensation-type-label">Type</InputLabel>
        <Select<string>
          labelId="compensation-type-label"
          id="compensation-type"
          value={compensationType}
          onChange={handleCompensationTypeChange}
          label="Type"
          size="small"
        >
          <MenuItem value="any">Any</MenuItem>
          <MenuItem value="hourly">Hourly</MenuItem>
          <MenuItem value="salary">Salary</MenuItem>
          <MenuItem value="commission">Commission</MenuItem>
        </Select>
      </FormControl>

      <Grid container spacing={2}>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Minimum"
            type="number"
            value={compensationMin}
            onChange={handleCompensationMinChange}
            placeholder="0"
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            label="Maximum"
            type="number"
            value={compensationMax}
            onChange={handleCompensationMaxChange}
            placeholder="No limit"
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default JobSearchCompensationInput;
